/**
 * Platform routes — generic, CMS/platform-owned routes that every site gets
 * identically, injected at the router level so no site hardcodes them.
 *
 * A site opts in by passing `platform` to mountClient / createRenderBody:
 *
 *   mountClient(App, { platform: { orgSlug, apiBase } })
 *
 * The shell mounts platform routes (currently the /g/:token client gallery
 * viewer) ahead of the site's own routes, which fall through the `/*` splat to
 * the site's <App> (its bespoke design routes). New platform routes are added
 * here once, never per-site.
 */
import type { ReactNode } from 'react'
import { Routes, Route } from 'react-router-dom'
import { ClientGalleryView } from './components/ClientGalleryView'

export interface PlatformOptions {
  /** This site's org slug (for public CMS API calls). */
  orgSlug: string
  /** CMS public API base, e.g. https://api.cms.fuguelab.app */
  apiBase: string
}

/**
 * Wrap the site's app element with the platform route table. `/g/:token` (and
 * future platform routes) win by specificity; everything else falls through the
 * splat to the site's own <App> and its design routes.
 */
export function PlatformShell({
  appElement,
  platform,
}: {
  appElement: ReactNode
  platform: PlatformOptions
}): ReactNode {
  return (
    <Routes>
      <Route path="/g/:token" element={<ClientGalleryView apiBase={platform.apiBase} orgSlug={platform.orgSlug} />} />
      <Route path="/*" element={appElement} />
    </Routes>
  )
}
